import { UserRoleCode } from '@mm/state/model/CompanyUsersModel';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import {
  currentUserRoleCodeSelector,
  getTwilioAvailable,
  selectIsShopifyContext,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { createSelector } from 'reselect';
import {
  SidebarTitleDefinition,
  SIDEBAR_MENU,
  SidebarMenuDefinition,
} from './BotBuilderSidebar/sidebar-menu';

const selectIsSysAdmin = (state: StoreState) => state.session.session.admin;

const getFilteredSidebarMenu = (
  isSysAdmin: boolean,
  featureFlags: ReturnType<typeof combinedFeatureFlagsSelector>,
  isTwillioAvailable: boolean,
  userRole: UserRoleCode,
  isShopify,
) => {
  const smsItemsByFeatureFlag = (section: SidebarTitleDefinition) => {
    if (section.id === 'SMS' && section.featureFlag) {
      return isTwillioAvailable || featureFlags[section.featureFlag];
    }
    return true;
  };

  const byHideFeatureFlag = (section: SidebarTitleDefinition) =>
    !(section.hideFeatureFlag && featureFlags[section.hideFeatureFlag]);

  const filterByUserRole = (section: SidebarTitleDefinition) =>
    userRole === 'monitor_leads'
      ? ['CONTACTS', 'AUDIENCES', 'OTHER'].includes(section.id)
      : userRole === 'live_chat_agent'
      ? false
      : true;

  const filterMenuByShopifyStatus = (menu: SidebarTitleDefinition) =>
    isShopify
      ? ['DASHBOARD', 'XRAY', 'CONTACTS', 'AUDIENCES', 'INTEGRATIONS'].includes(
          menu.id,
        )
      : true;

  const filterSubMenuByShopifyStatus = (menuItem: SidebarMenuDefinition) =>
    isShopify
      ? [
          'native-integrations',
          'contact-suppression',
          'notifications',
          'xray',
        ].includes(menuItem.id)
      : true;

  const filteredSideBar = SIDEBAR_MENU.filter(byHideFeatureFlag)
    .filter(smsItemsByFeatureFlag)
    .filter(filterMenuByShopifyStatus)
    .filter(filterByUserRole);

  return filteredSideBar.map(sidebarSection => {
    const menuItems = sidebarSection.menuItems
      ?.filter(menuItem => {
        if (
          menuItem.hideFeatureFlag &&
          !featureFlags[menuItem.hideFeatureFlag]
        ) {
          return false;
        }

        if (menuItem.mustBeSysAdmin) {
          return isSysAdmin;
        }

        if (menuItem.hideForRole?.includes(userRole)) {
          return false;
        }
        return true;
      })
      .filter(filterSubMenuByShopifyStatus);
    return {
      ...sidebarSection,
      menuItems,
    };
  });
};

export const filteredSidebarMenuSelector = createSelector(
  [
    selectIsSysAdmin,
    combinedFeatureFlagsSelector,
    getTwilioAvailable,
    currentUserRoleCodeSelector,
    selectIsShopifyContext,
  ],
  getFilteredSidebarMenu,
);
